// ─────────────────────────────────────────────────────────────────────────────
// Frontend auth/data client — Supabase-compatible shim over the BuildersLab API.
//
// The app was built against `@supabase/supabase-js`. Rather than rewrite every
// call site, this module re-implements the exact surface the app uses
// (`supabase.auth.*`, `supabase.from(...)`, `supabase.storage.*`,
// `supabase.channel(...)`) backed by our own JWT auth + REST API on the same
// origin. The session (JWT + user) lives in localStorage.
// ─────────────────────────────────────────────────────────────────────────────

const API_URL = import.meta.env.VITE_API_URL ?? '';
const SESSION_KEY = 'bl_session';

// ── session storage ──────────────────────────────────────────────────────────
function readSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    return s && s.access_token ? s : null;
  } catch {
    return null;
  }
}

function writeSession(session) {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* private-mode: ignore */
  }
}

// ── auth-state listeners ─────────────────────────────────────────────────────
const listeners = new Set();
function emit(event, session) {
  for (const cb of listeners) {
    try { cb(event, session); } catch { /* ignore listener errors */ }
  }
}

async function apiFetch(path, { method = 'GET', body, auth = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const s = readSession();
    if (s?.access_token) headers.Authorization = `Bearer ${s.access_token}`;
  }
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  return { res, data };
}

// ── auth API ─────────────────────────────────────────────────────────────────
const auth = {
  async getSession() {
    return { data: { session: readSession() }, error: null };
  },

  async getUser() {
    const s = readSession();
    return { data: { user: s?.user ?? null }, error: null };
  },

  onAuthStateChange(callback) {
    listeners.add(callback);
    // Supabase emits an initial event asynchronously; mirror that so consumers
    // that rely on it settle correctly.
    const s = readSession();
    setTimeout(() => callback('INITIAL_SESSION', s), 0);
    return {
      data: {
        subscription: {
          unsubscribe() { listeners.delete(callback); },
        },
      },
    };
  },

  async signInWithPassword({ email, password }) {
    const { res, data } = await apiFetch('/api/auth/login', { method: 'POST', body: { email, password } });
    if (!res.ok) return { data: { user: null, session: null }, error: { message: data?.error || 'Sign in failed.' } };
    const session = { access_token: data.access_token, user: data.user };
    writeSession(session);
    emit('SIGNED_IN', session);
    return { data: { user: data.user, session }, error: null };
  },

  async signUp({ email, password, options }) {
    const full_name = options?.data?.full_name;
    const { res, data } = await apiFetch('/api/auth/signup', { method: 'POST', body: { email, password, full_name } });
    if (!res.ok) return { data: { user: null, session: null }, error: { message: data?.error || 'Sign up failed.' } };
    const session = { access_token: data.access_token, user: data.user };
    writeSession(session);
    emit('SIGNED_IN', session);
    return { data: { user: data.user, session }, error: null };
  },

  async signOut() {
    apiFetch('/api/auth/logout', { method: 'POST', auth: true }).catch(() => {});
    writeSession(null);
    emit('SIGNED_OUT', null);
    return { error: null };
  },

  async updateUser(attrs = {}) {
    const body = {};
    if (attrs.password) body.password = attrs.password;
    // Required by the server to change an existing password.
    if (attrs.current_password) body.current_password = attrs.current_password;
    if (attrs.data?.full_name !== undefined) body.full_name = attrs.data.full_name;
    const { res, data } = await apiFetch('/api/auth/update', { method: 'POST', body, auth: true });
    if (!res.ok) return { data: { user: null }, error: { message: data?.error || 'Update failed.', field: data?.field } };
    // Keep the cached session user in sync.
    const s = readSession();
    if (s && data.user) { s.user = data.user; writeSession(s); }
    emit('USER_UPDATED', s);
    return { data: { user: data.user }, error: null };
  },

  /**
   * Re-read the signed-in user from the server and refresh the copy kept in
   * the session, keeping the same token.
   *
   * The session user is a snapshot taken at sign-in. Without this, a name or
   * photo changed in Settings stayed stale everywhere that reads the session —
   * the navbar, the workspace, the dashboard greeting — until the next sign-in.
   */
  async refreshSession() {
    const s = readSession();
    if (!s) return { data: { session: null, user: null, profile: null }, error: { message: 'Not signed in.' } };
    try {
      const { res, data } = await apiFetch('/api/auth/me', { auth: true });
      if (!res.ok) return { data: { session: s, user: s.user, profile: null }, error: { message: data?.error || 'Refresh failed.' } };
      const next = { ...s, user: data.user };
      writeSession(next);
      emit('USER_UPDATED', next);
      return { data: { session: next, user: data.user, profile: data.profile || null }, error: null };
    } catch {
      return { data: { session: s, user: s.user, profile: null }, error: { message: 'Could not reach BuildersLab.' } };
    }
  },

  /**
   * Replace the session outright — used when the server hands back a new
   * token, as it does after an email change (the old token names the old
   * address).
   */
  async setSession({ access_token, user }) {
    if (!access_token || !user) return { data: { session: null }, error: { message: 'Missing token.' } };
    const session = { access_token, user };
    writeSession(session);
    emit('USER_UPDATED', session);
    return { data: { session }, error: null };
  },

  async resetPasswordForEmail(email) {
    const { res, data } = await apiFetch('/api/auth/reset-password', { method: 'POST', body: { email } });
    if (!res.ok) return { data: null, error: { message: data?.error || 'Request failed.' } };
    return { data: {}, error: null };
  },

  /**
   * The other half of the reset: exchange the emailed token for a new password.
   *
   * Deliberately unauthenticated — the whole point is that the person cannot
   * sign in. The token is the credential, and the server hashes and expiry-checks
   * it before touching the account.
   */
  async confirmPasswordReset({ token, password }) {
    // apiFetch throws on a network failure rather than returning a response, and
    // this function promises a {data, error} pair. Without the catch the
    // rejection escapes into the caller's submit handler, which leaves the form
    // stuck on "Saving…" with nothing said — the one state a password reset must
    // never end in, because the user cannot sign in to try another way.
    try {
      const { res, data } = await apiFetch('/api/auth/reset-password/confirm', {
        method: 'POST', body: { token, password },
      });
      if (!res.ok) return { data: null, error: { message: data?.error || 'Could not reset your password.' } };
      return { data: {}, error: null };
    } catch {
      return { data: null, error: { message: 'Could not reach BuildersLab. Check your connection and try again.' } };
    }
  },

  /**
   * Complete a social sign-in with a provider ID token.
   *
   * The token is verified server-side — the browser never asserts who it is.
   * Sign-up and sign-in are the same call: a first-time email creates the
   * account, a returning one signs in, and `is_new_user` says which happened so
   * the UI can greet a new person properly.
   */
  async signInWithIdToken({ provider, idToken, firstName, lastName }) {
    const { res, data } = await apiFetch('/api/auth/oauth', {
      method: 'POST',
      body: { provider, id_token: idToken, first_name: firstName, last_name: lastName },
    });
    if (!res.ok) {
      return { data: { user: null, session: null }, error: { message: data?.error || 'Sign in failed.' } };
    }
    const session = { access_token: data.access_token, user: data.user };
    writeSession(session);
    emit('SIGNED_IN', session);
    return { data: { user: data.user, session, isNewUser: !!data.is_new_user }, error: null };
  },

  // Redirect-style OAuth is not used — see signInWithIdToken above.
  async signInWithOAuth() {
    return { data: null, error: { message: 'Use signInWithIdToken for social sign-in.' } };
  },
};

// ── minimal .from() query builder (profiles only, always self-scoped) ─────────
class FrontendQuery {
  constructor(table) {
    this.table = table;
    this.op = 'select';
    this.payload = null;
  }
  // The columns and the filter are accepted for call-site compatibility and
  // ignored: this client only ever reads or writes the caller's own row.
  select(_columns) { return this; }
  update(payload) { this.op = 'update'; this.payload = payload; return this; }
  eq(_column, _value) { return this; }
  order() { return this; }
  limit() { return this; }
  single() { this._single = true; return this; }
  maybeSingle() { this._single = true; return this; }

  async _run() {
    if (this.table !== 'profiles') {
      return { data: null, error: { message: `Unsupported table in frontend client: ${this.table}` } };
    }
    if (this.op === 'update') {
      const { res, data } = await apiFetch('/api/me', { method: 'PATCH', body: this.payload, auth: true });
      if (!res.ok) return { data: null, error: { message: data?.error || 'Update failed.' } };
      return { data: data.profile, error: null };
    }
    const { res, data } = await apiFetch('/api/me', { auth: true });
    if (!res.ok) return { data: null, error: { message: data?.error || 'Fetch failed.' } };
    return { data: data.profile, error: null };
  }

  then(onFulfilled, onRejected) { return this._run().then(onFulfilled, onRejected); }
  catch(onRejected) { return this._run().catch(onRejected); }
}

// ── storage shim (image uploads → /api/upload) ───────────────────────────────
const _publicUrlCache = new Map();

function storageFrom(_bucket) {
  return {
    async upload(path, file, opts = {}) {
      try {
        const dataUrl = await fileToDataUrl(file);
        const folder = path.startsWith('logos/') ? 'logos' : 'avatars';
        const { res, data } = await apiFetch('/api/upload', {
          method: 'POST',
          body: { data_url: dataUrl, folder },
          auth: true,
        });
        if (!res.ok) return { data: null, error: { message: data?.error || 'Upload failed.' } };
        _publicUrlCache.set(path, data.publicUrl);
        return { data: { path }, error: null };
      } catch (err) {
        return { data: null, error: { message: err.message || 'Upload failed.' } };
      }
    },
    getPublicUrl(path) {
      return { data: { publicUrl: _publicUrlCache.get(path) || '' } };
    },
  };
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── realtime shim (no-op; the app also fetches on mount) ──────────────────────
function channel() {
  const stub = { on() { return stub; }, subscribe() { return stub; }, unsubscribe() { return stub; } };
  return stub;
}

export const supabase = {
  auth,
  from: (table) => new FrontendQuery(table),
  storage: { from: storageFrom },
  channel,
  removeChannel() { /* no-op */ },
};
